import React, { useEffect, useContext, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { IconArrowOutlineDown, IconArrowOutlineRight } from 'components/Zui/Icon'
import { format } from 'utils/datetime'
import { arrayDifference } from 'utils/helpers'
import { useToggle, useIntl } from 'hooks'
import { FileContext } from 'features/share-details/Details'
import {
    getFileThumbnails,
    thumbnailSelector,
} from 'features/patient-file-thumbnail/thumbnailSlice'
import Thumbnail from './Thumbnail'
import { Checkbox, Divider, ScrollableDirective } from 'components/Zui'

import './index.css'

const ThumbnailsContainer = ({ data, filter, defaultOpen, handleSelectFile, handleClick }) => {
    const { files } = data
    const reduxDispatch = useDispatch()
    const { isOpen, onToggle } = useToggle(defaultOpen)
    const { isOpen: isMoreDropdownOpen, onToggle: onMoreDropdownToggle } = useToggle()
    const { selectable } = useContext(FileContext)
    const [filteredFiles, setFilteredFiles] = useState([])

    const thumbnailCache = useSelector(state => thumbnailSelector.selectIds(state))
    const { isLoading } = useSelector(state => state.thumbnails)

    useEffect(() => {
        if (filteredFiles && isOpen) {
            const fileIds = filteredFiles.map(f => f.thumbnailId).filter(f => f)
            const difference = arrayDifference(fileIds, thumbnailCache)

            if (fileIds.length && difference.length) {
                reduxDispatch(getFileThumbnails(difference.join() + '&IsSharedView=true'))
            }
        }
    }, [filteredFiles, isOpen])

    useEffect(() => {
        if (files) {
            let filtered = files
            if (filter !== 'all') {
                filtered = files.filter(f => f.fileType === filter)
            }

            setFilteredFiles(filtered)
        }
        return () => setFilteredFiles([])
    }, [files, filter])

    const arrayDiffWithFilteredFiles = array => {
        return arrayDifference(
            filteredFiles.map(f => f.fileId),
            array
        )
    }

    const isSelected = () => {
        const arrDiffLen = arrayDiffWithFilteredFiles(selectable.selectedFiles).length

        if (arrDiffLen && arrDiffLen !== filteredFiles.length) {
            return 'mixed'
        }
        if (!Boolean(arrDiffLen) && (files === undefined || Boolean(filteredFiles.length))) {
            return true
        }
        return false
    }

    const handleSelect = val => {
        const fileIds = filteredFiles.map(f => f.fileId)
        handleSelectFile(val, fileIds)
    }

    return (
        <Header
            onToggle={onToggle}
            isOpen={isOpen}
            data={data}
            handleSelect={handleSelect}
            isSelected={isSelected()}
            onMoreDropdownToggle={onMoreDropdownToggle}
            isMoreDropdownOpen={isMoreDropdownOpen}
        >
            {isOpen && filteredFiles.length > 0 && (
                <div className="accordion-data-wrapper">
                    <div className="accordion-data-container">
                        {filteredFiles.map(file => {
                            return (
                                <Thumbnail
                                    data={file}
                                    key={file.fileId}
                                    handleSelectFile={handleSelectFile}
                                    isLoading={isLoading}
                                    handleClick={handleClick}
                                />
                            )
                        })}
                    </div>
                </div>
            )}
        </Header>
    )
}

const Header = ({
    children,
    onToggle,
    isOpen,
    data,
    handleSelect,
    isSelected,
    onMoreDropdownToggle,
    isMoreDropdownOpen,
}) => {
    const { t } = useIntl()
    const arrowIcon = isOpen ? (
        <IconArrowOutlineDown className="arrow" size="s" />
    ) : (
        <IconArrowOutlineRight className="arrow" size="s" />
    )
    return (
        <div className="header-wrap">
            <div className="header-header">
                <div className="header-data">
                    <div className="header-open-arrow" onClick={onToggle}>
                        {arrowIcon}
                    </div>
                    <Checkbox
                        style={{ display: 'flex', fontSize: '14px' }}
                        name="terms"
                        theme="zbds-dark"
                        enableMixed
                        value={isSelected}
                        onChange={e => handleSelect(e.target.value)}
                    >
                        <span className="checkbox-label" style={{ color: '#D1D9E5' }}>
                            {format(data.shareDate)}
                        </span>
                    </Checkbox>

                    <Divider orientation="vertical" size="s" emphasis="highlight" />
                    <div className="accordion-sharedby-wrapper">
                        <div className="accordion-sharedby-content-xl">
                            <div className="accordion-content-text">
                                <span>
                                    <span className="accordion-text sharedby">
                                        {t('sharedby')}{' '}
                                    </span>
                                    <span className="accordion-text name">
                                        {data.sharedBy.slice(0, 2).join(', ')}
                                    </span>
                                </span>
                            </div>

                            {data.sharedBy.length > 2 && (
                                <div className="multiple-tags-dropdown">
                                    {isMoreDropdownOpen && (
                                        <div className="tags-drop-down-item-list">
                                            <ScrollableDirective
                                                style={{ maxHeight: '320px' }}
                                                theme="zbds-dark"
                                            >
                                                {data.sharedBy.slice(2).map(user => {
                                                    return (
                                                        <div
                                                            key={user}
                                                            className="tag-drop-down-item"
                                                        >
                                                            <span>{user}</span>
                                                        </div>
                                                    )
                                                })}
                                            </ScrollableDirective>
                                        </div>
                                    )}
                                    <div
                                        className="tag-item-wrapper"
                                        onClick={onMoreDropdownToggle}
                                    >
                                        <div className="multi-tag-wrapper">
                                            <span data-ref="tagPills">
                                                +{data.sharedBy.length - 2}
                                            </span>
                                            <div className="tags-open-icon">
                                                <IconArrowOutlineDown size="s" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="accordion-sharedby-content-m">
                            <div className="accordion-content-text">
                                <span>
                                    <span className="accordion-text sharedby">
                                        {t('sharedby')}{' '}
                                    </span>
                                    <span className="accordion-text name">
                                        {data.sharedBy.slice(0, 1).join(', ')}
                                    </span>
                                </span>
                            </div>

                            {data.sharedBy.length > 1 && (
                                <div className="multiple-tags-dropdown">
                                    {isMoreDropdownOpen && (
                                        <div className="tags-drop-down-item-list">
                                            <ScrollableDirective
                                                style={{ maxHeight: '320px' }}
                                                theme="zbds-dark"
                                            >
                                                {data.sharedBy.slice(1).map(user => {
                                                    return (
                                                        <div
                                                            key={user}
                                                            className="tag-drop-down-item"
                                                        >
                                                            <span>{user}</span>
                                                        </div>
                                                    )
                                                })}
                                            </ScrollableDirective>
                                        </div>
                                    )}
                                    <div
                                        className="tag-item-wrapper"
                                        onClick={onMoreDropdownToggle}
                                    >
                                        <div className="multi-tag-wrapper">
                                            <span data-ref="tagPills">
                                                +{data.sharedBy.length - 1}
                                            </span>
                                            <div className="tags-open-icon">
                                                <IconArrowOutlineDown size="s" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {data.patientName && (
                        <>
                            <Divider orientation="vertical" size="s" emphasis="highlight" />

                            <div className="accordion-content-text">
                                <span className="accordion-text">
                                    {' '}
                                    {t('patient')}: {data.patientName}
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </div>
            {children}
        </div>
    )
}

export default ThumbnailsContainer
